/**
 * Authentication Service
 * Handles user registration, login, logout, and profile management
 */

import api, { TokenStorage, restoreSession as apiRestoreSession } from './apiClient';

/**
 * Application roles (constant, not editable)
 */
export const AppRoles = {
  Admin: 'admin',
  ContentCreator: 'content-creator',
  MasterTrainer: 'master-trainer',
  Trainer: 'trainer',
  Trainee: 'trainee',
};

/**
 * Check if user has a specific role
 * @param {Object} user - User object with roles array
 * @param {string} role - Role to check
 * @returns {boolean}
 */
export const hasRole = (user, role) => {
  return user?.roles?.includes(role) ?? false;
};

/**
 * Check if user has any of the specified roles
 * @param {Object} user - User object with roles array
 * @param {string[]} roles - Roles to check
 * @returns {boolean}
 */
export const hasAnyRole = (user, roles) => {
  return roles.some(role => hasRole(user, role));
};

/**
 * Register a new user
 * Note: Users now register without passwords - admin approval is required.
 * Upon approval, user receives email with "Set Your Password" link.
 * @param {Object} userData - Registration data
 * @param {string} userData.email - User email
 * @param {string} [userData.firstName] - First name
 * @param {string} [userData.lastName] - Last name
 * @param {string} [userData.preferredLanguage] - Language code (en, de, nl, fr)
 * @param {boolean} userData.termsAccepted - Must be true to accept T&C
 * @param {number} [userData.surveyParticipantId] - ID from survey completion (if user filled registration survey)
 * @returns {Promise<Object>} Response indicating registration is pending approval
 */
export const register = async (userData) => {
  const response = await api.post('/auth/register', userData, { suppressNotification: true });
  // Note: No tokens are returned - user must wait for admin approval
  return response;
};

/**
 * Login with email and password
 * Note: Refresh token is now automatically stored in httpOnly cookie by the server
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {boolean} [rememberMe=false] - If true, extends session from 7 to 30 days
 * @returns {Promise<Object>} Auth response with access token and user, or 2FA/password change requirement
 */
export const login = async (email, password, rememberMe = false) => {
  const response = await api.post('/auth/login', { email, password, rememberMe }, { suppressNotification: true });
  
  // If 2FA is required, return the response without storing tokens
  if (response.requiresTwoFactor) {
    return {
      success: true,
      requiresTwoFactor: true,
      twoFactorUserId: response.twoFactorUserId,
      message: response.message
    };
  }
  
  // If password change is required (admin-enforced)
  if (response.passwordChangeRequired) {
    return {
      success: true,
      passwordChangeRequired: true,
      userId: response.userId,
      email: response.email,
      message: response.message
    };
  }
  
  if (response.success) {
    // Access token stored in memory, refresh token in httpOnly cookie (automatic)
    TokenStorage.setAccessToken(response.accessToken, response.expiration);
    TokenStorage.setUser(response.user);
  }
  
  return response;
};

/**
 * Change password during forced password change (before login completes)
 * Used when admin requires user to change password
 * @param {string} userId - User ID from login response
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Password confirmation
 * @returns {Promise<Object>} Auth response with tokens on success
 */
export const forcedPasswordChange = async (userId, currentPassword, newPassword, confirmPassword) => {
  const response = await api.post('/auth/forced-password-change', {
    userId,
    currentPassword,
    newPassword,
    confirmPassword,
  }, { suppressNotification: true });
  
  if (response.success) {
    TokenStorage.setAccessToken(response.accessToken, response.expiration);
    TokenStorage.setUser(response.user);
  }
  
  return response;
};

/**
 * Logout current user
 * Clears tokens and notifies backend
 */
export const logout = async () => {
  try {
    if (TokenStorage.hasTokens()) {
      await api.post('/auth/logout', {}, { suppressNotification: true });
    }
  } catch (error) {
    // Ignore errors on logout
    console.warn('Logout API call failed:', error);
  } finally {
    TokenStorage.clear();
  }
};

/**
 * Get current user profile from API
 * @returns {Promise<Object|null>} User data or null
 */
export const getCurrentUser = async () => {
  try {
    if (!TokenStorage.getAccessToken()) return null;
    
    return await api.get('/auth/me', {}, { suppressNotification: true });
  } catch (error) {
    console.error('Failed to get current user:', error);
    return null;
  }
};

/**
 * Update current user profile
 * @param {Object} updates - Profile updates
 * @param {string} [updates.firstName] - First name
 * @param {string} [updates.lastName] - Last name
 * @param {string} [updates.phoneNumber] - Phone number
 * @param {string} [updates.preferredLanguage] - Language code
 * @returns {Promise<Object>} Updated user data
 */
export const updateProfile = async (updates) => {
  const response = await api.put('/auth/me', updates);
  TokenStorage.setUser(response);
  return response;
};

/**
 * Upload or update profile image
 * @param {File} file - Image file to upload
 * @param {Object} [options] - Request options
 * @param {Function} [options.onUploadProgress] - Progress callback
 * @returns {Promise<Object>} Updated user data
 */
export const uploadProfileImage = async (file, options = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.upload('/auth/me/profile-image', formData, {
    successMessage: 'Profile image updated successfully',
    ...options,
  });
  TokenStorage.setUser(response);
  return response;
};

/**
 * Delete profile image
 * @returns {Promise<Object>} Updated user data
 */
export const deleteProfileImage = async () => {
  const response = await api.delete('/auth/me/profile-image', {
    successMessage: 'Profile image removed',
  });
  TokenStorage.setUser(response);
  return response;
};

/**
 * Change password for current user
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Password confirmation
 * @returns {Promise<Object>} Response
 */
export const changePassword = async (currentPassword, newPassword, confirmPassword) => {
  return await api.post('/auth/change-password', {
    currentPassword,
    newPassword,
    confirmPassword,
  }, { 
    suppressNotification: true,
    successMessage: 'Password changed successfully',
  });
};

/**
 * Check if user is currently authenticated
 * Uses confirmed-session state instead of cached user data.
 * @returns {boolean} True if this tab has a confirmed auth session
 */
export const isAuthenticated = () => {
  return TokenStorage.hasTokens() || TokenStorage.hasConfirmedSession();
};

/**
 * Get cached user from localStorage
 * @returns {Object|null} User data or null
 */
export const getCachedUser = () => {
  return TokenStorage.getUser();
};

/**
 * Attempt to restore an existing session after page refresh.
 * Uses the httpOnly refresh token cookie to get new tokens.
 * @returns {Promise<Object|null>} User data if session was restored, null otherwise
 */
export const restoreSession = async () => {
  try {
    const user = await apiRestoreSession();
    return user;
  } catch (error) {
    console.warn('Session restoration failed:', error);
    return null;
  }
};

/**
 * Request password reset link via email
 * @param {string} email - User email
 * @returns {Promise<Object>} Response
 */
export const forgotPassword = async (email) => {
  return await api.post('/auth/forgot-password', { email }, { suppressNotification: true });
};

/**
 * Reset password using token from email
 * @param {string} email - User email
 * @param {string} token - Reset token from email link
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Password confirmation
 * @returns {Promise<Object>} Response
 */
export const resetPassword = async (email, token, newPassword, confirmPassword) => {
  return await api.post('/auth/reset-password', {
    email,
    token,
    newPassword,
    confirmPassword,
  }, { suppressNotification: true });
};

/**
 * Validate password reset token before showing the form
 * @param {string} email - User email
 * @param {string} token - Reset token from email link
 * @returns {Promise<{isValid: boolean, message?: string}>} Validation result
 */
export const validateResetToken = async (email, token) => {
  return await api.post('/auth/validate-reset-token', {
    email,
    token,
  }, { suppressNotification: true });
};

/**
 * Validate set password token before showing the form
 * Used for newly approved users setting their initial password
 * @param {string} userId - User ID
 * @param {string} token - Set password token from approval email
 * @returns {Promise<{isValid: boolean, message?: string}>} Validation result
 */
export const validateSetPasswordToken = async (userId, token) => {
  return await api.post('/auth/validate-set-password-token', {
    userId,
    token,
  }, { suppressNotification: true });
};

/**
 * Confirm email address using token from confirmation email
 * @param {string} userId - User ID
 * @param {string} token - Email confirmation token
 * @returns {Promise<{message: string}>} Confirmation result
 */
export const confirmEmail = async (userId, token) => {
  return await api.post('/auth/confirm-email', {
    userId,
    token,
  }, { suppressNotification: true });
};

// ======================
// Two-Factor Authentication (2FA)
// ======================

/**
 * Get 2FA setup information (shared key and QR code URI)
 * @returns {Promise<{sharedKey: string, authenticatorUri: string}>}
 */
export const get2FASetup = async () => {
  return await api.get('/auth/2fa/setup', {}, { suppressNotification: true });
};

/**
 * Enable 2FA after verifying the setup code
 * @param {string} verificationCode - 6-digit code from authenticator app
 * @returns {Promise<{recoveryCodes: string[]}>} Recovery codes to save
 */
export const enable2FA = async (verificationCode) => {
  return await api.post('/auth/2fa/enable', { verificationCode }, { suppressNotification: true });
};

/**
 * Disable 2FA (requires current password and TOTP code)
 * @param {string} password - Current password
 * @param {string} verificationCode - 6-digit code from authenticator app
 * @returns {Promise<Object>}
 */
export const disable2FA = async (password, verificationCode) => {
  return await api.post('/auth/2fa/disable', { password, verificationCode }, { suppressNotification: true });
};

/**
 * Verify 2FA code during login
 * @param {string} userId - User ID from login response
 * @param {string} verificationCode - 6-digit code from authenticator app
 * @param {boolean} [rememberDevice=false] - Remember this device for 30 days (skip 2FA)
 * @param {boolean} [rememberMe=false] - Extend session to 30 days
 * @returns {Promise<Object>} Auth response with tokens
 */
export const verify2FA = async (userId, verificationCode, rememberDevice = false, rememberMe = false) => {
  const response = await api.post('/auth/2fa/verify', {
    userId,
    verificationCode,
    rememberDevice,
    rememberMe,
  }, { suppressNotification: true });
  
  if (response.success) {
    TokenStorage.setAccessToken(response.accessToken, response.expiration);
    TokenStorage.setUser(response.user);
  }
  
  return response;
};

/**
 * Login using a recovery code (when authenticator app is unavailable)
 * @param {string} userId - User ID from login response
 * @param {string} recoveryCode - One of the 10 recovery codes
 * @param {boolean} [rememberMe=false] - Extend session to 30 days
 * @returns {Promise<Object>} Auth response with tokens
 */
export const loginWithRecoveryCode = async (userId, recoveryCode, rememberMe = false) => {
  const response = await api.post('/auth/2fa/recovery-login', {
    userId,
    recoveryCode,
    rememberMe,
  }, { suppressNotification: true });
  
  if (response.success) {
    TokenStorage.setAccessToken(response.accessToken, response.expiration);
    TokenStorage.setUser(response.user);
  }
  
  return response;
};

/**
 * Generate new recovery codes (invalidates all existing codes)
 * @returns {Promise<{recoveryCodes: string[]}>} New recovery codes
 */
export const regenerateRecoveryCodes = async () => {
  return await api.post('/auth/2fa/recovery-codes', {}, { 
    suppressNotification: true,
    successMessage: 'New recovery codes generated',
  });
};

/**
 * Get current 2FA status
 * @returns {Promise<{isEnabled: boolean, recoveryCodesRemaining: number}>}
 */
export const get2FAStatus = async () => {
  return await api.get('/auth/2fa/status', {}, { suppressNotification: true });
};

// ======================
// Session Management
// ======================

/**
 * Get all active sessions for the current user
 * @returns {Promise<{sessions: Array, currentSessionId: string, totalSessions: number}>}
 */
export const getSessions = async () => {
  return await api.get('/auth/sessions', {}, { suppressNotification: true });
};

/**
 * Revoke a specific session by ID
 * @param {string} sessionId - Session ID to revoke
 * @returns {Promise<{success: boolean, message: string, revokedCount: number}>}
 */
export const revokeSession = async (sessionId) => {
  return await api.delete(`/auth/sessions/${sessionId}`, {}, { 
    suppressNotification: false,
    successMessage: 'Session revoked successfully',
  });
};

/**
 * Revoke all other sessions (logout other devices)
 * @returns {Promise<{success: boolean, message: string, revokedCount: number}>}
 */
export const revokeOtherSessions = async () => {
  return await api.post('/auth/sessions/revoke-others', {}, { 
    suppressNotification: false,
    successMessage: 'Other sessions revoked successfully',
  });
};

/**
 * Revoke all sessions including current (logout everywhere)
 * This will also clear the local session.
 * @returns {Promise<{success: boolean, message: string, revokedCount: number}>}
 */
export const revokeAllSessions = async () => {
  const response = await api.post('/auth/sessions/revoke-all', {}, { 
    suppressNotification: false,
    successMessage: 'All sessions revoked. You have been logged out.',
  });
  
  // Clear local session data
  if (response.success) {
    TokenStorage.clearAll();
  }
  
  return response;
};

// ======================
// Trusted Devices (Remember Device for 30 days)
// ======================

/**
 * Get all trusted devices for the current user
 * @returns {Promise<{devices: Array, totalCount: number}>}
 */
export const getTrustedDevices = async () => {
  return await api.get('/auth/trusted-devices', {}, { suppressNotification: true });
};

/**
 * Revoke a specific trusted device by ID
 * @param {string} deviceId - Trusted device ID to revoke
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const revokeTrustedDevice = async (deviceId) => {
  return await api.delete(`/auth/trusted-devices/${deviceId}`, {}, { 
    suppressNotification: false,
    successMessage: 'Trusted device revoked successfully',
  });
};

/**
 * Revoke all trusted devices (require 2FA on all devices)
 * @returns {Promise<{success: boolean, message: string, revokedCount: number}>}
 */
export const revokeAllTrustedDevices = async () => {
  return await api.post('/auth/trusted-devices/revoke-all', {}, { 
    suppressNotification: false,
    successMessage: 'All trusted devices revoked',
  });
};

// ======================
// Login History
// ======================

/**
 * Get login history for the current user
 * Supports optional date range filtering
 * @param {Date|string|null} startDate - Optional start date for filtering
 * @param {Date|string|null} endDate - Optional end date for filtering
 * @returns {Promise<{events: Array, totalCount: number}>}
 */
export const getLoginHistory = async (startDate = null, endDate = null) => {
  const params = {};
  
  if (startDate) {
    params.startDate = startDate instanceof Date 
      ? startDate.toISOString() 
      : new Date(startDate).toISOString();
  }
  
  if (endDate) {
    params.endDate = endDate instanceof Date 
      ? endDate.toISOString() 
      : new Date(endDate).toISOString();
  }

  const queryString = Object.keys(params).length > 0
    ? '?' + new URLSearchParams(params).toString()
    : '';

  return await api.get(`/auth/login-history${queryString}`, {}, { suppressNotification: true });
};

/**
 * Set initial password for a newly approved user
 * @param {string} userId - User email/identifier from URL
 * @param {string} token - Set password token from URL
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Confirm password
 * @returns {Promise<Object>} Response indicating success or failure
 */
export const setInitialPassword = async (userId, token, newPassword, confirmPassword) => {
  return await api.post('/auth/set-password', { 
    userId, 
    token, 
    newPassword, 
    confirmPassword 
  }, { suppressNotification: true });
};

const authService = {
  register,
  login,
  logout,
  getCurrentUser,
  updateProfile,
  uploadProfileImage,
  deleteProfileImage,
  changePassword,
  forcedPasswordChange,
  isAuthenticated,
  getCachedUser,
  restoreSession,
  forgotPassword,
  resetPassword,
  validateResetToken,
  validateSetPasswordToken,
  setInitialPassword,
  confirmEmail,
  // 2FA methods
  get2FASetup,
  enable2FA,
  disable2FA,
  verify2FA,
  loginWithRecoveryCode,
  regenerateRecoveryCodes,
  get2FAStatus,
  // Session management methods
  getSessions,
  revokeSession,
  revokeOtherSessions,
  revokeAllSessions,
  // Trusted device methods
  getTrustedDevices,
  revokeTrustedDevice,
  revokeAllTrustedDevices,
  // Login history
  getLoginHistory,
  hasRole,
  hasAnyRole,
  AppRoles,
};

export default authService;
